// The surveys list's search (consistency-pass spec §4): free text over the
// name, Status, and Template — a manifest kore's SearchBar renders, and the
// mapping from its chips to surveys_search.rs's typed filters.

import type { BoolOp, FilterResponse, IdOp } from "@pej/kore/components";
import type { SurveyFilters, SurveySearchQueryField, SurveySortField } from "@/api/surveys";
import type { Template } from "@/api/types";
import type { RawFilters } from "@/features/results/responses-filters";
import { acceptedOp } from "@/features/search/wire-op";

export const SURVEY_SORT_FIELDS: readonly SurveySortField[] = ["Name", "CreatedAt"];
export const SURVEY_DEFAULT_ORDER = { field: "Name", order: "Asc" } as const;
export const SURVEY_QUERY_FIELDS: readonly SurveySearchQueryField[] = ["Name"];
export const SURVEY_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export const SURVEY_FILTER_PARAMS = ["active", "template"] as const;

/** `templates: null` while they load: the Template filter then knows only
 *  the ids the URL names, each with an empty label, so a chip shows nothing
 *  for its value rather than the raw id until the names arrive. */
export function surveysManifest(templates: readonly Template[] | null, urlIds: readonly string[] = []): FilterResponse {
    return {
        // Read by SearchBar's derived placeholder because `query.fields` is absent.
        query: { placeholder: "Search surveys…" },
        filters: [
            {
                type: "Enum",
                filterParam: "active",
                label: "Status",
                operators: ["Eq"],
                values: [
                    { value: "true", label: "Active" },
                    { value: "false", label: "Inactive" },
                ],
            },
            {
                type: "Enum",
                filterParam: "template",
                label: "Template",
                operators: ["Eq"],
                values: templates
                    ? templates.map((template) => ({ value: template.id, label: template.name }))
                    : urlIds.map((id) => ({ value: id, label: "" })),
            },
        ],
    };
}

/** What kore reads for the list: the URL, minus a Status that isn't exactly
 *  one of Active / Inactive. Status takes one value ("Is Active" or "Is
 *  Inactive"); a URL naming both — hand-edited, or two picks in kore's
 *  multi-select picker — is no filter at all, so it shows no chip either.
 *  kore's next patch rewrites every manifest key, which clears it from the
 *  URL too. */
export function withOneStatus(search: Record<string, unknown>): Record<string, unknown> {
    const raw = search.active;
    if (raw === undefined) return search;
    const values = (Array.isArray(raw) ? raw : [raw])
        .flatMap((value) => String(value).split(","))
        .map((value) => value.trim());
    if (values.length === 1 && (values[0] === "true" || values[0] === "false")) return search;
    return Object.fromEntries(Object.entries(search).filter(([key]) => key !== "active"));
}

export function surveyFiltersForWire(raw: RawFilters | undefined): SurveyFilters {
    const chips = raw ?? {};
    const wire: SurveyFilters = {};
    // withOneStatus already keeps kore to one Status value; anything else
    // (both, say) is still no filter here, never a guess.
    const status = acceptedOp<{ type: "Eq"; values: string[] }>(chips.active, ["Eq"]);
    if (status && status.values.length === 1)
        wire.active = { type: "Eq", value: status.values[0] === "true" } satisfies BoolOp;
    const template = acceptedOp<IdOp>(chips.template, ["Eq"]);
    if (template && template.values.length > 0) wire.template = { type: "Eq", values: template.values };
    return wire;
}
